import React, { useMemo, useState } from 'react'

export type SearchableSelectItem = {
  value: string
  label: string
}

function normalizeSearch(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

function filterItems(items: SearchableSelectItem[], query: string): SearchableSelectItem[] {
  const key = normalizeSearch(query.trim())
  if (!key) return items
  return items.filter((item) => normalizeSearch(item.label).startsWith(key))
}

interface SearchableSelectProps {
  items: SearchableSelectItem[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function LocationFieldButton({
  label,
  value,
  placeholder,
  disabled,
  onOpen,
}: {
  label: string
  value: string
  placeholder: string
  disabled?: boolean
  onOpen: () => void
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1">{label}</label>
      <button
        type="button"
        disabled={disabled}
        onClick={onOpen}
        className="w-full px-3 py-2.5 rounded-xl border border-muted bg-background text-left disabled:opacity-50"
      >
        <span className={value ? 'text-foreground' : 'text-muted-foreground'}>
          {value || placeholder}
        </span>
      </button>
    </div>
  )
}

/** Full-height picker that fills its parent (use inside a flex modal). */
export function SelectPanel({
  title,
  items,
  value,
  query,
  onQueryChange,
  onSelect,
  onBack,
  placeholder,
  emptyLabel = 'No matches',
}: {
  title: string
  items: SearchableSelectItem[]
  value: string
  query: string
  onQueryChange: (value: string) => void
  onSelect: (value: string) => void
  onBack: () => void
  placeholder?: string
  emptyLabel?: string
}) {
  const filtered = useMemo(() => filterItems(items, query), [items, query])

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-background">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground px-2 py-1 rounded-lg"
          aria-label="Back"
        >
          ←
        </button>
        <h3 className="font-display font-semibold text-foreground">{title}</h3>
      </div>
      <div className="px-4 pt-3 shrink-0">
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={placeholder}
          autoFocus
          autoComplete="off"
          className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-foreground"
        />
      </div>
      <ul className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
        {filtered.length === 0 ? (
          <li className="px-3 py-3 text-sm text-muted-foreground">{emptyLabel}</li>
        ) : (
          filtered.map((item) => (
            <li key={item.value}>
              <button
                type="button"
                className={`w-full text-left px-3 py-3 rounded-xl text-sm ${
                  item.value === value
                    ? 'bg-primary/20 text-foreground font-medium'
                    : 'text-foreground hover:bg-muted/50'
                }`}
                onClick={() => onSelect(item.value)}
              >
                {item.label}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  )
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  items,
  value,
  onChange,
  placeholder,
  disabled,
  className = 'w-full px-3 py-2 rounded-xl border border-muted bg-background text-foreground',
}) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const selected = items.find((item) => item.value === value)
  const filtered = useMemo(() => filterItems(items, query), [items, query])

  return (
    <div className="relative">
      <input
        type="search"
        disabled={disabled}
        value={open ? query : (selected?.label ?? '')}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => {
          if (disabled) return
          setOpen(true)
          setQuery('')
        }}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 120)
        }}
        className={`${className} disabled:opacity-50`}
      />
      {open && !disabled && (
        <ul className="absolute left-0 right-0 z-[80] mt-1 max-h-64 overflow-auto rounded-xl border border-border bg-background shadow-xl">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">No matches</li>
          ) : (
            filtered.map((item) => (
              <li key={item.value}>
                <button
                  type="button"
                  className={`w-full text-left px-3 py-2.5 text-sm hover:bg-muted/60 ${
                    item.value === value ? 'bg-primary/15 text-foreground font-medium' : 'text-foreground'
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange(item.value)
                    setQuery('')
                    setOpen(false)
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}

export default SearchableSelect
